import { useAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { Navigate } from "react-router-dom";

import { MOBILE_ONBOARDING_INFO_SCREEN_PATH, WELCOME_PATH } from "../../../../constants/env";
import { OnboardingIntroExperimentVariant } from "../../../../constants/onboarding-intro-experiment-variant.enum";
import MobileOnboardingIntro from "../../../../features/survey/components/MobileOnboardingIntro/MobileOnboardingIntro";
import { mobileOnboardingIntroServedAtom } from "../../../../features/survey/store/onboarding-intro-experiment.store";
import { useOnboardingIntroExperiment } from "../../../../hooks/feature-flags/use-onboarding-intro-experiment/useOnboardingIntroExperiment";
import { Loading } from "../../../../legacy/components/Loading";
import { assignOnboardingIntroExperiment } from "../../../../lib/mixpanel/experiments/onboarding-intro-experiment/assign-onboarding-intro-experiment";
import { useMobileOnboardingGuard } from "../hooks/useMobileOnboardingGuard";

interface MobileOnboardingGuardProps {
  servesIntro?: boolean;
}

export function MobileOnboardingGuard({ servesIntro = false }: Readonly<MobileOnboardingGuardProps>): JSX.Element {
  const { isLogged, loading, error, onboardingPath } = useMobileOnboardingGuard();
  const experimentVariant = useOnboardingIntroExperiment();
  const [isIntroServed, setIsIntroServed] = useAtom(mobileOnboardingIntroServedAtom);

  useEffect(
    function applyExperimentAssignment() {
      const isExperimentUnresolved = experimentVariant === undefined;
      if (!isLogged || isExperimentUnresolved) {
        return;
      }
      assignOnboardingIntroExperiment(experimentVariant);
    },
    [isLogged, experimentVariant]
  );

  const handleContinue = useCallback(
    function onContinue() {
      setIsIntroServed(true);
    },
    [setIsIntroServed]
  );

  if (loading) {
    return <Loading />;
  }

  if (error !== null) {
    throw error;
  }

  if (!isLogged) {
    return <Navigate to={WELCOME_PATH} replace />;
  }

  const isExperimentUnresolved = experimentVariant === undefined;
  if (isExperimentUnresolved) {
    return <Loading />;
  }

  const shouldServeIntro = experimentVariant === OnboardingIntroExperimentVariant.INTRO_SCREEN && !isIntroServed;
  if (shouldServeIntro && !servesIntro) {
    return <Navigate to={MOBILE_ONBOARDING_INFO_SCREEN_PATH} replace />;
  }
  if (shouldServeIntro) {
    return <MobileOnboardingIntro onContinue={handleContinue} />;
  }

  return <Navigate to={onboardingPath} replace />;
}

export default MobileOnboardingGuard;
